import { useEffect, useRef } from 'react'
import { useI18n, type Locale } from '../i18n/useI18n'
import { languages } from '../i18n/languages'
import { useStore } from '../store/useStore'
import { api } from '../services/api'

/** 連打の途中を送らないための待ち時間。最後に選んだものだけが残る。 */
const SAVE_DEBOUNCE_MS = 500

export default function LanguageSwitcher(
  { className = '', showLabels = false }:
  { className?: string
    /** #2115 国旗だけでなく言語名も出す。**どの言語かを読んで選べるようにする**ため。
     *  国旗は端末によっては国コード (CN / KR) にしか見えず、中国語が簡体字か
     *  繁体字かも分からない。場所に余裕のあるメニューのページで使う。 */
    showLabels?: boolean },
) {
  const { locale, setLocale } = useI18n()
  const userId = useStore((s) => s.userId)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
    },
    [],
  )

  /**
   * #2005 選んだ言語をサーバにも残す。
   *
   * 以前は端末内 (i18next の localStorage) に閉じていて、users doc の `language` は
   * **登録時に焼いた 'en' のまま**だった。訪日客向けサービスとして「何語の人が
   * 使っているか」はいちばん見たい数字なのに、集計すると嘘になる状態だった。
   *
   * ログインしていなければ送らない (ゲストには保存先が無い)。**結果は見ない** —
   * 保存に失敗しても画面の言語は切り替わるべきで、切り替えを止める方が害が大きい。
   */
  const choose = (next: Locale) => {
    setLocale(next)
    if (!userId) return
    // **連打の途中は送らない。** 投げっぱなしだと POST の到着順が入れ替わり、
    // 画面に出ていない言語が残ることがある。少し待って最後の 1 回だけ送る。
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      void api.updateProfile(userId, { language: next }).catch(() => {})
    }, SAVE_DEBOUNCE_MS)
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => choose(lang.code)}
          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all tap-effect ${
            locale === lang.code
              ? 'bg-yokoso-red text-white shadow-sm'
              : 'bg-white/80 text-text-secondary hover:bg-white'
          }`}
          title={lang.label}
          aria-label={lang.label}
        >
          {showLabels ? `${lang.flag} ${lang.label}` : lang.flag}
        </button>
      ))}
    </div>
  )
}
